import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { StatusBadge } from "@/components/shared/StatusBadge";
import type { Prospect, ProspectField } from "@/hooks/useProspects";
import { PROSPECT_STATUS_META } from "@/lib/prospectDisplay";
import { CORE_CELL_FIELDS, type CoreCellKey } from "./prospectColumns";
import { ProspectCell } from "./ProspectCell";

/**
 * Painel lateral de um lead frio: todos os campos básicos e as colunas da
 * lista (inclusive ocultas) editáveis, e o atalho "Mover para o funil".
 * Não grava: `onSaveCore`/`onSaveData` vão para o hook no pai.
 */
export interface ProspectSheetProps {
  prospect: Prospect | null;
  listName: string;
  fields: ProspectField[];
  onOpenChange: (open: boolean) => void;
  onSaveCore: (key: CoreCellKey, value: unknown) => void;
  onSaveData: (key: string, value: unknown) => void;
  onPromote: () => void;
}

export function ProspectSheet({
  prospect,
  listName,
  fields,
  onOpenChange,
  onSaveCore,
  onSaveData,
  onPromote,
}: ProspectSheetProps) {
  const status = prospect ? PROSPECT_STATUS_META[prospect.status] : null;
  return (
    <Sheet open={!!prospect} onOpenChange={onOpenChange}>
      <SheetContent className="flex w-full flex-col gap-0 overflow-y-auto p-0 sm:max-w-[520px]">
        {prospect && status && (
          <>
            <SheetHeader className="gap-2.5 border-b border-border px-6 pb-4 pt-6 text-left">
              <SheetDescription>{listName}</SheetDescription>
              <SheetTitle className="font-display text-[24px] font-bold tracking-[-0.02em]">
                {prospect.name}
              </SheetTitle>
              <div className="flex flex-wrap items-center gap-2">
                <StatusBadge tone={status.tone} dot>
                  {status.label}
                </StatusBadge>
              </div>
              {prospect.lead_id ? (
                <Button asChild variant="outline" className="h-10">
                  <Link to={`/admin/leads/${prospect.lead_id}`}>
                    Abrir lead no funil <ArrowRight className="ml-1.5 h-4 w-4" aria-hidden="true" />
                  </Link>
                </Button>
              ) : (
                <Button type="button" className="h-10" onClick={onPromote}>
                  Mover para o funil <ArrowRight className="ml-1.5 h-4 w-4" aria-hidden="true" />
                </Button>
              )}
            </SheetHeader>
            <div className="flex flex-col gap-5 px-6 py-5">
              <section aria-labelledby="sheet-core">
                <h3
                  id="sheet-core"
                  className="mb-2 text-[11px] font-bold uppercase tracking-[0.06em] text-muted-foreground"
                >
                  Contato
                </h3>
                <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                  {CORE_CELL_FIELDS.map((f) => (
                    <div
                      key={f.key}
                      className={f.type === "long_text" ? "sm:col-span-2" : undefined}
                    >
                      <div className="mb-1 text-[11px] text-muted-foreground">{f.label}</div>
                      <ProspectCell
                        variant="block"
                        field={f}
                        value={prospect[f.key]}
                        onSave={(v) => onSaveCore(f.key, v)}
                      />
                    </div>
                  ))}
                </div>
              </section>
              {fields.length > 0 && (
                <section aria-labelledby="sheet-custom">
                  <h3
                    id="sheet-custom"
                    className="mb-2 text-[11px] font-bold uppercase tracking-[0.06em] text-muted-foreground"
                  >
                    Colunas da lista
                  </h3>
                  <div className="flex flex-col gap-2.5">
                    {fields.map((f) => (
                      <div key={f.id}>
                        <div className="mb-1 text-[11px] text-muted-foreground">
                          {f.label}
                          {f.hidden ? " · oculta" : ""}
                        </div>
                        <ProspectCell
                          variant="block"
                          field={f}
                          value={prospect.data[f.key]}
                          onSave={(v) => onSaveData(f.key, v)}
                        />
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
